import React, {useEffect} from "react";
import BasicPage from "../../common/basicPage/BasicPage";
import {useNavigate, useSearchParams} from "react-router-dom";
import {appRoutePaths} from "../../../config/config";
import Button from "../../common/Button";
import {settingsStore} from "../../../redux/settings/settingsStore";
import nativeBridge from "../../../services/nativeBridge";
import {useAppSelector} from "../../../redux/hooks";
import {logger} from "../../../services/log/logger";
import {PDCStatus} from "../../../redux/types/pdcStatus";

interface IDisclosureAndConsentProps {
}

export default function DisclosureAndConsent({}: IDisclosureAndConsentProps) {
  const headerTextContent: string = "Support Your Journey"
  const navigate = useNavigate();
  const pdcStatus = useAppSelector(settingsStore.getPDCStatus);
  const isPdcConsentApplicable = useAppSelector(settingsStore.getIsPdcConsentApplicable);
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');
  const nextPage = from ? from : appRoutePaths.infoPage;

  // Section: If PDC is not applicable, go to another page.
  useEffect(() => {
    if (!isPdcConsentApplicable) {
      settingsStore.setPDCStatus('notApplicable');
    }
  }, [isPdcConsentApplicable]);

  // Navigation must be done in a useEffect to avoid React warnings and unexpected behavior.
  // For example, if we don't do it here, and the PDC page reloads (say b/c the user temporarily put it in the background),
  // then the user could get stuck on this page even if they had already made a choice.
  useEffect(() => {
    if (pdcStatus !== "unknown") {
      // User has already made a choice directly or through UMP so skip this page
      navigate(nextPage, {replace: true}); // No back button should take the user back to this PDC page.
    }
  }, [pdcStatus, navigate, nextPage]);

  // Section: When user makes a choice, save it, inform native, and go to onboarding.
  const setPDCStatusAndNavigateToNextPage = (status: PDCStatus) => {
    settingsStore.setPDCStatus(status);
    logger.webviewToNativeMessage.info({
      message: `Webview to native:`,
      data: [
        { key: "type", value: "disclosureAndConsent" },
        { key: "status", value: status },
      ]
    });
    nativeBridge.sendMessageToNative({type: "disclosureAndConsent", consentGiven: status === "granted"});
    // navigate(nextPage); // Navigation is handled by a useEffect above when the pdcStatus in the Redux store changes.
  }

  return (
    <BasicPage
      portraitModeHeaderText={headerTextContent}
      showBackButton={false}
      content={
        <>
          <h1>{headerTextContent}</h1>
          <p>Allow <strong>&quot;Playground: Real Piano Lessons&quot;</strong> to track your activity across other companies&apos; apps and websites?</p>
          <p>This helps us support your musical journey and show personalized offers and updates. We won’t show any ads within the app.</p>
          <br />
          <Button
            onClick={() => {
              setPDCStatusAndNavigateToNextPage("granted");
            }}
            text={"Allow"}
            minWidth={200}
            buttonType={"secondary"}
          />
          <Button
            onClick={() => {
              setPDCStatusAndNavigateToNextPage("denied");
            }}
            text={"Don't Allow"}
            minWidth={200}
            buttonType={"primary"}
          />
        </>
      }
    />
  );
}
